// Drug cart drawer: searchable list -> selected drug card with editable dose -> Draw / Push.
// Dose validation happens on the server (harm engine). Here we just present a clear UI and
// require a two-tap confirm so accidental mis-doses are caught.

import { useMemo, useState } from 'react';
import Drawer from './Drawer';
import { DRUGS, category_label, type DrugDef } from './drug-catalogue';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdmin: (drug: string, dose_mg: number, route: string, indication: string) => void;
}

export default function DrugCart({ open, onClose, onAdmin }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DrugDef | null>(null);
  const [dose, setDose] = useState<number>(0);
  const [drawn, setDrawn] = useState(false);

  const grouped = useMemo(() => {
    const filt = search.trim().toLowerCase();
    const list = filt ? DRUGS.filter((d) => d.display.toLowerCase().includes(filt) || d.indication.includes(filt)) : DRUGS;
    const groups = new Map<string, DrugDef[]>();
    for (const d of list) {
      const arr = groups.get(d.category) || [];
      arr.push(d);
      groups.set(d.category, arr);
    }
    return Array.from(groups.entries());
  }, [search]);

  function pickDrug(d: DrugDef) {
    setSelected(d);
    setDose(d.defaultDoseMg);
    setDrawn(false);
  }

  function clearSelection() {
    setSelected(null);
    setDose(0);
    setDrawn(false);
  }

  function push() {
    if (!selected) return;
    onAdmin(selected.drug, dose, selected.route, selected.indication);
    clearSelection();
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title="Drug cart" width="lg">
      {selected ? (
        <DoseCard
          drug={selected}
          dose={dose}
          setDose={setDose}
          drawn={drawn}
          setDrawn={setDrawn}
          onCancel={clearSelection}
          onPush={push}
        />
      ) : (
        <>
          <label className="block mb-3">
            <span className="text-xs uppercase tracking-widest text-mono-white/40">Search</span>
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-1 w-full bg-ink-800 border border-ink-800 rounded px-3 py-2 text-mono-white focus:border-brand-600 focus:outline-none"
              placeholder="adrenaline · amiodarone · atropine · ..."
            />
          </label>

          {grouped.map(([cat, drugs]) => (
            <section key={cat} className="mb-5">
              <h3 className="text-xs uppercase tracking-widest text-mono-white/40 mb-2">{category_label(cat as DrugDef['category'])}</h3>
              <div className="space-y-1">
                {drugs.map((d, i) => (
                  <button
                    key={`${d.drug}-${i}`}
                    onClick={() => pickDrug(d)}
                    className="w-full text-left p-3 rounded bg-ink-800 hover:bg-ink-950 transition-colors border border-transparent hover:border-brand-600"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium">{d.display}</span>
                      <span className="text-xs text-mono-white/40 font-mono shrink-0">{d.route}</span>
                    </div>
                    <div className="text-xs text-mono-white/50 mt-1">{d.indication.replace(/_/g, ' ')}</div>
                    {d.highRisk && <span className="text-[10px] text-alarm-amber uppercase tracking-widest">⚠ High-risk</span>}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </Drawer>
  );
}

function DoseCard({ drug, dose, setDose, drawn, setDrawn, onCancel, onPush }: {
  drug: DrugDef;
  dose: number;
  setDose: (n: number) => void;
  drawn: boolean;
  setDrawn: (b: boolean) => void;
  onCancel: () => void;
  onPush: () => void;
}) {
  // Out-of-range warning. Heuristic — server is the authority.
  const min = drug.defaultDoseMg * 0.5;
  const max = drug.defaultDoseMg * 2;
  const outOfRange = dose < min || dose > max;
  const tenX = dose >= drug.defaultDoseMg * 10;

  // Format dose. <1 → mcg; otherwise mg.
  const display = dose < 1 ? `${(dose * 1000).toFixed(0)} mcg` : `${dose} mg`;

  return (
    <div>
      <div className="mb-4">
        <button onClick={onCancel} className="text-xs text-mono-white/50 hover:text-mono-white">← back to list</button>
      </div>
      <div className="panel p-4 bg-ink-800/50">
        <h3 className="text-lg font-semibold">{drug.display}</h3>
        <p className="text-xs text-mono-white/50 mt-1">{drug.indication.replace(/_/g, ' ')} · {drug.route}</p>
        {drug.notes && <p className="text-xs text-mono-white/70 mt-2 italic">{drug.notes}</p>}
        {drug.warningMd && (
          <div className="mt-3 p-2 bg-alarm-amber/10 text-alarm-amber text-xs rounded border-l-2 border-alarm-amber">
            ⚠ {drug.warningMd}
          </div>
        )}
        <div className="mt-3 text-[10px] text-mono-white/30 uppercase tracking-widest">
          Guideline range: {drug.defaultDoseMg < 1 ? `${(drug.defaultDoseMg * 1000).toFixed(0)} mcg` : `${drug.defaultDoseMg} mg`} typical
          {' '}· RCUK Adult ALS 2021
        </div>
      </div>

      <div className="mt-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs uppercase tracking-widest text-mono-white/40">Dose</span>
          {outOfRange && (
            <span className={`text-xs ${tenX ? 'text-alarm-red' : 'text-alarm-amber'}`}>
              {tenX ? '!! 10× the standard dose — confirm intent' : 'Outside typical range'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setDose(Math.max(0, +(dose - drug.doseStep).toFixed(4)))}
            disabled={drawn}
            className="btn-ghost text-xl px-4 disabled:opacity-30"
          >−</button>
          <input
            type="number"
            value={dose}
            step={drug.doseStep}
            min={0}
            max={drug.doseMaxMg}
            onChange={(e) => setDose(Math.max(0, Number(e.target.value)))}
            disabled={drawn}
            className="bg-ink-800 border border-ink-800 rounded px-3 py-2 text-2xl tabular-nums text-mono-white font-mono w-32 text-center focus:border-brand-600 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={() => setDose(Math.min(drug.doseMaxMg, +(dose + drug.doseStep).toFixed(4)))}
            disabled={drawn}
            className="btn-ghost text-xl px-4 disabled:opacity-30"
          >+</button>
          <button
            onClick={() => setDose(drug.defaultDoseMg)}
            disabled={drawn}
            className="btn-ghost text-xs disabled:opacity-30"
          >reset</button>
        </div>
        <div className="text-3xl font-mono text-mono-green tabular-nums mt-3">{display}</div>
      </div>

      <div className="mt-8 space-y-2">
        {!drawn ? (
          <button onClick={() => setDrawn(true)} className="btn-primary w-full">
            Draw {display} {drug.route}
          </button>
        ) : (
          <>
            <button
              onClick={onPush}
              className={`w-full btn ${tenX ? 'bg-alarm-red hover:brightness-110 text-white' : 'btn-primary'}`}
            >
              Push {display} {drug.route} → confirm
            </button>
            <button onClick={() => setDrawn(false)} className="btn-ghost w-full text-sm">
              Cancel — back to draw
            </button>
          </>
        )}
      </div>
    </div>
  );
}
